/**
 * Phase 10D: Error taxonomy & user-facing messages (EN/AR).
 * Map error codes or API responses to message + action.
 */

const locale = (typeof window !== 'undefined' && localStorage.getItem('locale')) || 'en';

const MESSAGES = {
  // Category 1: Network
  api_timeout: {
    en: 'Request timed out. Check your connection and try again.',
    ar: 'انتهت مهلة الطلب. تحقق من اتصالك وحاول مرة أخرى',
    action: 'retry',
  },
  no_internet: {
    en: 'No internet connection. Showing cached data from {timestamp}.',
    ar: 'لا يوجد اتصال بالإنترنت. عرض البيانات المخزنة من {timestamp}',
    action: 'sync_when_online',
  },
  server_unreachable: {
    en: "Service temporarily unavailable. We're working on it.",
    ar: 'الخدمة غير متاحة مؤقتاً. نحن نعمل على حل المشكلة',
    action: 'try_again_1min',
  },
  // Category 2: Auth
  token_expired: {
    en: 'Session expired. Please log in again.',
    ar: 'انتهت الجلسة. يرجى تسجيل الدخول مرة أخرى',
    action: 'redirect_login',
  },
  unauthorized: {
    en: "You don't have permission to view this policy.",
    ar: 'ليس لديك إذن لعرض هذه البوليصة',
    action: 'back_to_renewals',
  },
  // Category 3: Data not found
  policy_not_found: {
    en: 'Policy not found. It may have been removed.',
    ar: 'البوليصة غير موجودة. ربما تم حذفها',
    action: 'view_all_renewals',
  },
  no_recommendations: {
    en: 'No better deals found. Your current policy is competitive.',
    ar: 'لم يتم العثور على صفقات أفضل. بوليصتك الحالية تنافسية',
    action: 'check_again',
    severity: 'info',
  },
  premium_history_empty: {
    en: 'Not enough data to show price trends. Check back after renewal.',
    ar: 'لا توجد بيانات كافية لإظهار اتجاهات الأسعار',
    action: null,
    severity: 'info',
  },
  // Category 4: Validation
  invalid_settings_max_price: {
    en: 'Max price increase must be between 0-100%',
    ar: 'يجب أن تكون الزيادة القصوى في السعر بين ٠-١٠٠٪',
    action: 'correct_input',
  },
  conflicting_settings: {
    en: 'Auto-switching requires at least 10% savings. Adjust your settings.',
    ar: 'التبديل التلقائي يتطلب توفير ١٠٪ على الأقل',
    action: 'adjust_settings',
  },
  share_rate_limit: {
    en: 'Daily sharing limit reached (5/day). Try again tomorrow at {time}.',
    ar: 'تم الوصول إلى حد المشاركة اليومي (٥/يوم)',
    action: 'wait',
  },
  rate_limit_check: {
    en: 'Rate limit exceeded. Max 5 checks per hour. Try again in {minutes} minutes.',
    ar: 'تم تجاوز حد المعدل. الحد الأقصى ٥ فحوصات في الساعة',
    action: 'wait',
  },
  // Category 5: Business logic
  policy_expired: {
    en: "This policy has expired. You cannot compare expired policies.",
    ar: 'هذه البوليصة منتهية الصلاحية. لا يمكنك مقارنة البوليصات المنتهية',
    action: 'remove_or_add',
  },
  check_in_progress: {
    en: 'Check already in progress. Please wait...',
    ar: 'الفحص قيد التقدم بالفعل. يرجى الانتظار...',
    action: 'wait',
    severity: 'info',
  },
  coverage_gap_too_large: {
    en: 'This switch creates a {gap}-day coverage gap (max allowed: {max} days). Adjust start date or settings.',
    ar: 'هذا التبديل يخلق فجوة تغطية {gap} يوماً',
    action: 'adjust_or_cancel',
  },
  insurer_api_partial: {
    en: "We couldn't reach {count} insurers. Results may be incomplete.",
    ar: 'لم نتمكن من الوصول إلى {count} شركات تأمين',
    action: 'view_partial',
    severity: 'warning',
  },
  // Category 6: UI/UX
  chart_unavailable: {
    en: 'Charts unavailable. Showing data as tables.',
    ar: 'الرسوم البيانية غير متاحة',
    action: 'view_tables',
    severity: 'info',
  },
  slow_connection: {
    en: 'Slow connection detected. Loading simplified view.',
    ar: 'اتصال بطيء. تحميل عرض مبسط',
    action: 'wait_or_cancel',
    severity: 'info',
  },
  // Generic
  generic_network: {
    en: 'Something went wrong. Check your connection and try again.',
    ar: 'حدث خطأ ما. تحقق من اتصالك وحاول مرة أخرى',
    action: 'retry',
  },
  failed_to_load_renewals: {
    en: 'Failed to load renewals',
    ar: 'فشل تحميل التجديدات',
    action: 'retry',
  },
};

/**
 * Get user-facing message for an error code. Replaces placeholders with params.
 * @param {string} code - Key from MESSAGES
 * @param {string} localeOverride - 'en' | 'ar'
 * @param {Object} params - e.g. { timestamp, time, gap, max, count }
 */
export function getErrorMessage(code, localeOverride = locale, params = {}) {
  const entry = MESSAGES[code] || MESSAGES.generic_network;
  const text = entry[localeOverride] || entry.en;
  if (!params || typeof text !== 'string') return text;
  return Object.keys(params).reduce((str, key) => str.replace(new RegExp(`\\{${key}\\}`, 'g'), params[key]), text);
}

/**
 * Get suggested action key for the error (for UI to show button).
 */
export function getErrorAction(code) {
  const entry = MESSAGES[code] || MESSAGES.generic_network;
  return entry.action || 'retry';
}

/**
 * Get severity: 'error' | 'warning' | 'info'.
 */
export function getErrorSeverity(code) {
  const entry = MESSAGES[code] || MESSAGES.generic_network;
  return entry.severity || 'error';
}

/**
 * Map API error (axios error) to a renewal error code and message.
 */
export function fromApiError(err, localeOverride = locale) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  const message = data?.message || data?.error?.message || err?.message || '';
  const retryAfter = data?.error?.retryAfter ?? data?.retryAfter;

  if (status === 401) return { code: 'token_expired', message: getErrorMessage('token_expired', localeOverride) };
  if (status === 403) return { code: 'unauthorized', message: getErrorMessage('unauthorized', localeOverride) };
  if (status === 404) return { code: 'policy_not_found', message: getErrorMessage('policy_not_found', localeOverride) };
  if (status === 429) {
    const minutes = retryAfter ? Math.ceil(retryAfter / 60) : 30;
    return { code: 'rate_limit_check', message: getErrorMessage('rate_limit_check', localeOverride, { minutes }), retryAfter };
  }
  if (status >= 500) return { code: 'server_unreachable', message: getErrorMessage('server_unreachable', localeOverride) };
  if (message.toLowerCase().includes('timeout')) return { code: 'api_timeout', message: getErrorMessage('api_timeout', localeOverride) };

  return { code: 'generic_network', message: getErrorMessage('generic_network', localeOverride) };
}

export default MESSAGES;
